import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import type { AsistenteRecipeResult, AsistenteTone } from '@workspace/types';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { platformAdminApi } from '../api/platformAdminApi';
import { useActiveTenants } from './recipe.hooks';
import { ClientFields, Question, RecipeDone, RecipeFooter, RecipePage } from './recipeParts';
import { defaultAssistantName } from './asistenteConserje.utils';

const KNOWLEDGE_MAX = 8000;

const TONES: Array<{ value: AsistenteTone; title: string; sample: string }> = [
  { value: 'CERCANO', title: 'Cercano', sample: '¡Hola! Abrimos de 8 a 18, ¿te espero?' },
  { value: 'FORMAL', title: 'Formal', sample: 'Buenos días. Atendemos de 8 a 18 horas.' },
];

export function AsistenteRecipePage() {
  const [error, setError] = useState('');
  const tenants = useActiveTenants(setError);
  const [tenantId, setTenantId] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [agentName, setAgentName] = useState('');
  const [knowledge, setKnowledge] = useState('');
  const [tone, setTone] = useState<AsistenteTone>('CERCANO');
  const [progress, setProgress] = useState('');
  const [result, setResult] = useState<AsistenteRecipeResult | null>(null);

  const answered = [Boolean(tenantId && companyName.trim()), Boolean(knowledge.trim()), true];
  const ready = answered.every(Boolean);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!ready || progress) return;
    setError('');
    setProgress('Creando el Asistente');
    try {
      setResult(
        await platformAdminApi.createAsistente({
          tenantId,
          companyName: companyName.trim(),
          agentName: agentName.trim() || undefined,
          knowledge: knowledge.trim(),
          tone,
        }),
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo crear el Asistente');
    } finally {
      setProgress('');
    }
  };

  if (result) {
    return (
      <RecipeDone
        title={`El Asistente de ${companyName} está creado`}
        subtitle='Responde con lo que le contaste y, si no sabe, ofrece que lo contacte una persona. Falta conectarle el WhatsApp.'
        steps={result.steps}
        aiReady={result.aiReady}
        actions={
          <>
            <Button asChild size='lg'>
              <Link to='/admin/whatsapp'>Conectar WhatsApp</Link>
            </Button>
            <Button asChild size='lg' variant='outline'>
              <Link to={`/admin/chatbot/bots/${result.botId}`}>Ver el Asistente</Link>
            </Button>
          </>
        }
      />
    );
  }

  return (
    <RecipePage
      title='Nuevo Asistente'
      intro='Un agente que responde las consultas de siempre con lo que la empresa le cuenta. Dos respuestas y queda listo.'
    >
      <form onSubmit={handleSubmit} noValidate>
        <ol className='relative'>
          <Question done={answered[0]} title='¿Para qué cliente?' hint='El Asistente queda dentro de su cuenta.'>
            <ClientFields
              tenants={tenants}
              tenantId={tenantId}
              onTenant={tenant => {
                setTenantId(tenant.id);
                setCompanyName(tenant.name);
              }}
              companyName={companyName}
              onCompanyName={setCompanyName}
              agentName={agentName}
              onAgentName={setAgentName}
              defaultAgentName={defaultAssistantName(companyName)}
            />
          </Question>

          <Question
            done={answered[1]}
            title='¿Qué tiene que saber?'
            hint='Horarios, dirección, productos, precios, formas de pago, preguntas que siempre hacen. Si no está acá, no lo inventa.'
          >
            <Label htmlFor='knowledge' className='sr-only'>
              Lo que tiene que saber
            </Label>
            <Textarea
              id='knowledge'
              rows={9}
              maxLength={KNOWLEDGE_MAX}
              value={knowledge}
              placeholder={
                'Atendemos de lunes a viernes de 8 a 18 y sábados de 9 a 13.\nEstamos en San Martín 1234.\nHacemos envíos en el día dentro de la ciudad.\nAceptamos transferencia, débito y Mercado Pago.'
              }
              onChange={event => setKnowledge(event.target.value)}
            />
            <p className='mt-1 text-right text-xs text-muted-foreground' aria-live='polite'>
              {knowledge.length.toLocaleString('es-AR')} / {KNOWLEDGE_MAX.toLocaleString('es-AR')}
            </p>
          </Question>

          <Question done last title='¿Cómo habla?' hint='Se puede cambiar después en la ficha del bot.'>
            <div role='radiogroup' aria-label='Tono' className='grid gap-2 sm:grid-cols-2'>
              {TONES.map(option => (
                <button
                  key={option.value}
                  type='button'
                  role='radio'
                  aria-checked={tone === option.value}
                  onClick={() => setTone(option.value)}
                  className={cn(
                    'rounded-lg border px-4 py-3 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    tone === option.value
                      ? 'border-primary bg-primary/5'
                      : 'border-input hover:border-foreground/30',
                  )}
                >
                  <span className='block text-sm font-medium text-foreground'>{option.title}</span>
                  <span className='mt-1 block text-sm text-muted-foreground'>{option.sample}</span>
                </button>
              ))}
            </div>
          </Question>
        </ol>

        <RecipeFooter error={error} progress={progress} answered={answered} submitLabel='Crear Asistente' />
      </form>
    </RecipePage>
  );
}
